import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { DataTable, Column } from "@/components/ui/data-table";
import {
  Download,
  Edit,
  Image,
  Loader2,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import { HotelService, HotelServiceProviderType } from "@/types/hotelService";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useHotelServiceFiltering } from "@/hooks/useHotelServiceFiltering";
import { useHotel } from "@/hooks/useHotel";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { useDeleteHotelService } from "@/hooks/useHotelServices";
import StatusCell from "./components/cells/StatusCell";
import RecommendedCell from "./components/cells/RecommendedCell";
import AvailabilityCell from "./components/cells/AvailabilityCell";
import HoursAvailabilityCell from "./components/cells/HoursAvailabilityCell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ServiceCategoriesPage from "./CategoriesPage";
import { useTranslation } from "react-i18next";
import TruncatedText from "@/components/common/TruncatedText";
import AssignedStaffCell from "@/components/common/AssignedStaffCell";
import { SettlementFiltersBar } from "../settlement/components/SettlementFiltersBar";
import ServiceFilterPopUp from "./components/ServiceFilterPopUp";
import ServicesStats from "./components/ServicesStats";
import PartnersServicesStats from "./components/PartnersServicesStats";
import { useServiceContext } from "@/contexts/ServiceContext";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import {
  hasCorePlatformEnabled,
  hasFrontdeskBlockPermission,
} from "@/utils/accessControl";
import {
  formatCurrencyAmount,
  getDiscountedServicePrice,
} from "@/components/pages/services/utils/priceFormatting";
import { ImportServicesModal } from "./components/ImportServicesModal";
import { hotelService } from "@/services/hotel-service-service";
import { downloadBlob } from "@/utils/downloadFile";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useGetPartnerSettings } from "@/components/pages/partner-integration-settings/hooks/partner-settings.hooks";
import { GuesteriaActivitiesForGuestsTab } from "@/components/pages/guesteria-partners/components/GuesteriaActivitiesForGuestsTab";
import { ServiceFeaturedSwitch } from "@/components/pages/guesteria-partners/components/ServiceFeaturedSwitch";
import { useServiceFeatured } from "@/components/pages/guesteria-partners/hooks/useServiceFeatured";

enum NavigationTabs {
  SERVICES = "SERVICES",
  CATEGORIES = "CATEGORIES",
}

// The tab identity IS the provider type — no separate enum needed (a
// distinct `enum ServiceTabs` with the same underlying string values used
// to live here, but casting one enum to another is never a clean TS
// conversion even when the values match, which was the tsc-gate failure
// this comment replaces: `activeTab as HotelServiceProviderType` at what
// were lines 463/616, TS2352).
const servicesTabsListClassName =
  "h-9 w-[685px] items-stretch rounded-lg bg-[#FBFAFF] py-1.5 px-1 gap-1 text-foreground shadow-sm";

const servicesTabsTriggerClassName =
  "flex flex-1 items-center justify-center self-stretch rounded-md border border-transparent bg-transparent px-2 py-1 text-xs font-medium leading-none text-[#0A0A0A] shadow-none transition-colors hover:bg-[#EDE9FE]/40 data-[state=active]:border-[#C7BCFF] data-[state=active]:bg-[#F2F0FF] data-[state=active]:shadow-none";

const serviceTabsByProvider: HotelServiceProviderType[] = [
  HotelServiceProviderType.GUEST_SERVICE,
  HotelServiceProviderType.EXTERNAL_SERVICE,
  HotelServiceProviderType.GUESTERIA_PARTNERS_SERVICE,
];

const activeTabFromProviderType = (
  providerType: string,
): HotelServiceProviderType =>
  serviceTabsByProvider.includes(providerType as HotelServiceProviderType)
    ? (providerType as HotelServiceProviderType)
    : HotelServiceProviderType.GUEST_SERVICE;

function resolveNavigationActiveTab(
  coreEnabled: boolean,
  searchParams: URLSearchParams,
): NavigationTabs {
  if (!coreEnabled) {
    return NavigationTabs.SERVICES;
  }
  const fromParam = searchParams.get("tab") as NavigationTabs | null;
  return fromParam ?? NavigationTabs.SERVICES;
}

export const ServicesPage = () => {
  const { hotelId, hotel, isLoading, error } = useHotel();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { filters, actions } = useServiceContext();
  const { userProfile } = useAuthenticatedUser();
  const coreEnabled = hasCorePlatformEnabled(userProfile);
  const initializedHotelIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hotelId) {
      return;
    }

    const rapidProvider = HotelServiceProviderType.GUESTERIA_PARTNERS_SERVICE;

    if (initializedHotelIdRef.current !== hotelId) {
      initializedHotelIdRef.current = hotelId;
      actions.resetFilters(
        coreEnabled ? HotelServiceProviderType.GUEST_SERVICE : rapidProvider,
      );
      return;
    }

    if (!coreEnabled && filters.providerType !== rapidProvider) {
      actions.resetFilters(rapidProvider);
    }
  }, [actions, coreEnabled, filters.providerType, hotelId]);

  const activeTab = resolveNavigationActiveTab(coreEnabled, searchParams);

  const showPartnerStatCards =
    activeTab === NavigationTabs.SERVICES &&
    (!coreEnabled ||
      filters.providerType ===
        HotelServiceProviderType.GUESTERIA_PARTNERS_SERVICE);

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  if (isLoading) {
    return <div>{t("services.page.loading", "Loading")}</div>;
  }

  if (error) {
    return (
      <div className="py-4 text-center text-red-600">
        {t("services.page.errors.hotelLoad", { message: error.message })}
      </div>
    );
  }

  if (
    initializedHotelIdRef.current !== hotelId ||
    (!coreEnabled &&
      filters.providerType !==
        HotelServiceProviderType.GUESTERIA_PARTNERS_SERVICE)
  ) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6 p-12">
      {showPartnerStatCards ? (
        <PartnersServicesStats
          hotelId={hotelId!}
          currency={hotel?.paymentSettings?.currency}
        />
      ) : (
        <ServicesStats currency={hotel?.paymentSettings?.currency} />
      )}

      {coreEnabled ? (
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <div className="mb-6 flex items-center justify-center">
            <TabsList className={servicesTabsListClassName}>
              <TabsTrigger
                value={NavigationTabs.SERVICES}
                className={servicesTabsTriggerClassName}
              >
                {t("services.page.tabs.services", "Services")}
              </TabsTrigger>
              <TabsTrigger
                value={NavigationTabs.CATEGORIES}
                className={servicesTabsTriggerClassName}
              >
                {t("services.page.tabs.categories", "Categories")}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value={NavigationTabs.SERVICES} className="mt-0">
            <HotelServices
              hotelId={hotelId!}
              currency={hotel?.paymentSettings?.currency}
              coreEnabled={coreEnabled}
            />
          </TabsContent>
          <TabsContent value={NavigationTabs.CATEGORIES} className="mt-0">
            <ServiceCategoriesPage />
          </TabsContent>
        </Tabs>
      ) : (
        <HotelServices
          hotelId={hotelId!}
          currency={hotel?.paymentSettings?.currency}
          coreEnabled={coreEnabled}
        />
      )}
    </div>
  );
};

export const HotelServices: React.FC<{
  hotelId: string;
  currency?: string;
  coreEnabled?: boolean;
}> = ({ hotelId, currency, coreEnabled = true }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { userProfile } = useAuthenticatedUser();
  // Changing the guest app's Featured list needs both Front Desk blocks
  // (`PATCH /mobile-settings` is guarded by `frontdesk_hotel_settings`).
  const canToggleFeatured =
    hasFrontdeskBlockPermission(
      userProfile,
      "frontdesk_management",
      "administrate",
    ) &&
    hasFrontdeskBlockPermission(
      userProfile,
      "frontdesk_hotel_settings",
      "administrate",
    );
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const {
    services,
    priceRange,
    isLoading,
    error,
    filters,
    actions,
    pagination,
  } = useHotelServiceFiltering(hotelId!);

  const activeTab = activeTabFromProviderType(filters.providerType);

  // The Featured switch exists only on the External tab: no hotel id, no
  // request for the guest-app settings on the other tabs.
  const featured = useServiceFeatured(
    activeTab === HotelServiceProviderType.EXTERNAL_SERVICE
      ? hotelId
      : undefined,
  );

  const { mutateAsync: deleteMutate, isPending: isLoadingDeletion } =
    useDeleteHotelService();

  // City name for the curated "Guesteria Partners Services" tab (task
  // 0027 follow-up): mirrors GuesteriaPartnersPage's own resolution.
  const { data: partnerSettings } = useGetPartnerSettings();
  const partnersCityName =
    partnerSettings?.destinationName ||
    partnerSettings?.destination?.destinationName ||
    undefined;

  const getAssignedStaffName = (service: HotelService) => {
    if (!service.staff) {
      return undefined;
    }

    return `${service.staff.profile?.firstName || ""} ${service.staff.profile?.lastName || ""}`.trim();
  };

  const isPartnersTab =
    activeTab === HotelServiceProviderType.GUESTERIA_PARTNERS_SERVICE;

  // Columns below only ever render for GUEST_SERVICES / EXTERNAL_SERVICES:
  // the GUESTERIA_PARTNERS_SERVICES tab no longer uses this DataTable at
  // all (see the curation UI branch in the render below), so no
  // partners-specific branching is needed here any more.
  const columns: Column<HotelService>[] = [
    {
      key: "status",
      header: t("services.page.table.status", "Status"),
      className: "text-center",
      render: (_, service: HotelService) => (
        <StatusCell id={service.id} hotelId={hotelId} status={service.status} />
      ),
      sortable: true,
    },
    ...(activeTab === HotelServiceProviderType.GUEST_SERVICE
      ? [
          {
            key: "isRecommended",
            header: t(
              "services.page.table.recommendedService",
              "Recommended service",
            ),
            className: "text-center",
            render: (_: unknown, service: HotelService) => (
              <div className="flex justify-center">
                <RecommendedCell
                  id={service.id}
                  hotelId={hotelId}
                  isRecommended={service.isRecommended}
                  canToggleRecommended={false}
                />
              </div>
            ),
          },
        ]
      : []),
    {
      key: "photo",
      header: t("services.page.table.photo", "Photo"),
      className: "text-center",
      render: (_, service: HotelService) => (
        <div className="mx-auto flex h-10 w-10 items-center justify-center overflow-hidden rounded-[8px] border border-gray-300 bg-gray-100">
          <PresignedImageWithPlaceholder
            className={"h-10 w-10 overflow-hidden object-cover"}
            photo={service.photoId}
            placeholder={<Image className="h-6 w-6 text-gray-400" />}
            alt={service.name}
          />
        </div>
      ),
    },
    {
      key: "name",
      header: t("services.page.table.name", "Name"),
      className: "text-center",
      render: (_, service: HotelService) => (
        // <span className="text-sm">{service.name}</span>
        <TruncatedText limit={30} className="text-xs font-semibold">
          {service.name}
        </TruncatedText>
      ),
      sortable: true,
    },
    {
      key: "category",
      header: t("services.page.table.category", "Category"),
      className: "text-center",
      sortable: true,
      render: (_, service: HotelService) => (
        <span className="bg-skyline-4 inline-flex h-6 w-fit max-w-full items-center justify-center rounded-2xl px-2 py-1 text-xs">
          {service.category.name}
        </span>
      ),
    },
    {
      key: "price",
      header: t("services.page.table.price", "Price"),
      className: "text-center",
      sortable: true,
      render: (_, service: HotelService) => (
        <span>
          {formatCurrencyAmount(
            getDiscountedServicePrice(service.price, service.discount),
            currency,
          )}
        </span>
      ),
    },
    {
      key: "availability",
      header: t("services.page.table.availability", "Availability"),
      className: "text-center",
      render: (_: unknown, service: HotelService) => (
        <AvailabilityCell availableDays={service.availableDays} />
      ),
    },
    {
      key: "hoursAvailability",
      header: t(
        "services.page.table.hoursAvailability",
        "Hours of availability",
      ),
      className: "text-center",
      render: (_: unknown, service: HotelService) => (
        <HoursAvailabilityCell
          availableFromTime={service.availableFromTime}
          availableToTime={service.availableToTime}
        />
      ),
    },
    ...(activeTab === HotelServiceProviderType.GUEST_SERVICE
      ? [
          {
            key: "responsible",
            header: t("services.page.table.responsible", "Responsible"),
            className: "text-center",
            render: (_: unknown, service: HotelService) => {
              const assignedTeamId = service.team?.id || service.teamId;
              const assignedTeamName = service.team?.name;
              const assignedStaffId = assignedTeamId
                ? undefined
                : service.staff?.id || service.staffId;
              const assignedStaffName = getAssignedStaffName(service);

              return (
                <AssignedStaffCell
                  teamId={assignedTeamId}
                  staffId={assignedStaffId}
                  teamName={assignedTeamName}
                  staffName={assignedStaffName}
                />
              );
            },
          },
        ]
      : []),
    ...(activeTab === HotelServiceProviderType.EXTERNAL_SERVICE
      ? [
          {
            key: "contractor",
            header: t("services.table.contractor", "Contractor"),
            className: "text-center",
            sortable: true,
            render: (_: any, service: HotelService) => (
              <div className="flex flex-col items-center">
                <span className="font-medium">
                  {service.contractor?.name} {service.contractor?.surname}
                </span>
                <span className="text-xs text-gray-500">
                  {service.contractor?.phone}
                </span>
              </div>
            ),
          },
          {
            key: "featured",
            header: t("services.page.table.featured", "Featured"),
            className: "text-center",
            render: (_: unknown, service: HotelService) => (
              <div className="flex justify-center">
                <ServiceFeaturedSwitch
                  serviceId={service.id}
                  featured={featured}
                  unavailableReason={
                    canToggleFeatured
                      ? null
                      : t("partners.curation.featured.noPermission")
                  }
                />
              </div>
            ),
          },
        ]
      : []),
    {
      key: "actions",
      header: t("services.page.table.actions", "Actions"),
      className: "text-center",
      render: (_: unknown, row: HotelService) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => navigate(`/services/${row.id}`)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handleDeleteService(row.id)}
            disabled={isLoadingDeletion}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const handleTabChange = (value: string) => {
    actions.handleFilterChange(actions.setProviderType, value);
  };

  const handleDeleteService = async (id: string) => {
    if (
      window.confirm(
        t(
          "services.page.actions.confirmDelete",
          "Are you sure you want to delete this service?",
        ),
      )
    ) {
      try {
        await deleteMutate({ id, hotelId });
      } catch (error) {
        console.error(
          t("services.page.errors.deleteFailed", "Failed to delete service:"),
          error,
        );
      }
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const providerType = activeTab;
      const blob = await hotelService.exportServices(
        hotelId,
        providerType,
        "csv",
      );
      downloadBlob(blob, `hotel-services-${providerType.toLowerCase()}.csv`);
      toast.success(
        t("services.page.export.success", "Services exported successfully"),
      );
    } catch (error) {
      console.error("Service export failed", error);
      toast.error(
        t("services.page.export.failed", "Failed to export services"),
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportSuccess = () => {
    actions.setCurrentPage(1);
    queryClient
      .invalidateQueries({ queryKey: ["hotelServices"] })
      .catch((error) => console.error("Failed to refresh services", error));
  };

  return (
    <div className="flex flex-col space-y-6">
      {coreEnabled && (
        <div className="flex items-center justify-center">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className={servicesTabsListClassName}>
              <TabsTrigger
                value={HotelServiceProviderType.GUEST_SERVICE}
                className={servicesTabsTriggerClassName}
              >
                {t("services.page.tabs.guestServices", "Guest services")}
              </TabsTrigger>
              <TabsTrigger
                value={HotelServiceProviderType.EXTERNAL_SERVICE}
                className={servicesTabsTriggerClassName}
              >
                {t("services.page.tabs.externalServices", "External services")}
              </TabsTrigger>
              <TabsTrigger
                value={HotelServiceProviderType.GUESTERIA_PARTNERS_SERVICE}
                className={servicesTabsTriggerClassName}
              >
                {t(
                  "services.page.tabs.partnersServices",
                  "Guesteria Partners Services",
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {isPartnersTab ? (
        // Task 0027 follow-up: this tab used to be a flat DataTable of
        // every GUESTERIA_PARTNERS_SERVICE-provider service with a
        // per-row enable/disable Switch (PATCH .../services/:id/enable|
        // disable). Disabling flipped a service to INACTIVE, which
        // dropped it out of the default status:ACTIVE filter — it just
        // vanished from the list instead of showing hidden-but-visible.
        // That toggle also had nothing to do with the curation overrides
        // model built for the "Activities for guests" tab on
        // GuesteriaPartnersPage. Reusing that same curation UI here
        // (hide/show through PUT .../partners/curation/overrides, hidden
        // items stay visible with a marker) replaces it entirely so this
        // tab has one consistent hide/show mechanism.
        <GuesteriaActivitiesForGuestsTab
          hotelId={hotelId}
          cityName={partnersCityName}
        />
      ) : (
        <>
          <div className="mt-4 flex items-center justify-between">
            <SettlementFiltersBar>
              <ServiceFilterPopUp
                actions={actions}
                filters={filters}
                priceRange={priceRange}
              />
            </SettlementFiltersBar>
            {coreEnabled ? (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  onClick={() => setIsImportOpen(true)}
                >
                  <Upload className="h-4 w-4" />
                  {t("services.page.actions.import", "Import")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  disabled={isExporting}
                  onClick={handleExport}
                >
                  {isExporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  {t("services.page.actions.export", "Export")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/services/add")}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t("services.page.actions.addService", "Add service")}
                </Button>
              </div>
            ) : null}
          </div>

          <DataTable
            data={services}
            getRowKey={(row) => row.id}
            columns={columns}
            loading={isLoading}
            pagination={{
              currentPage: pagination.currentPage,
              totalPages: pagination.totalPages,
              onPageChange: pagination.goToPage,
            }}
            sorting={{
              field: filters.sortField,
              direction: filters.sortDirection,
              onSort: actions.handleSort,
            }}
            emptyMessage={t(
              "services.page.table.empty",
              "No services found matching your criteria",
            )}
          />

          {error && (
            <div className="py-4 text-center text-red-600">
              {t("services.page.errors.servicesLoad", {
                message: error.message,
              })}
            </div>
          )}

          <ImportServicesModal
            hotelId={hotelId}
            providerType={activeTab}
            open={isImportOpen}
            onClose={() => setIsImportOpen(false)}
            onSuccess={handleImportSuccess}
          />
        </>
      )}
    </div>
  );
};
